import React, 
       { useState, 
         useMemo, 
         useEffect,
         useCallback
       }                              from 'react';
import { Box,
         Card,
         CardContent,
         FormControl,
         FormControlLabel,
         InputLabel,
         MenuItem,
         Select,
         Switch,
         TextField,
         Typography,
         Alert,
         Checkbox,
         ListItemText,
         Divider,
         Link,
         SelectChangeEvent,
       }                              from '@mui/material';
import { useForm, 
         Controller, 
         FieldErrors 
       }                              from 'react-hook-form';
import { MEASUREMENT_TYPE
       }                              from 'constants/global';
import { ApiErrorInfo
       }                              from 'types';
import { BglCloseButton, 
         BglPrimaryButton
       }                              from 'components/button/BglButtons';
import { APIData,
       }                              from 'types/cms/frontendTypes';
import { debounce }                   from 'lodash';
import { useGlobalConfig }            from 'context/GlobalContext';
import SaveIcon                       from '@mui/icons-material/Save';
import RestoreIcon                    from '@mui/icons-material/Restore';
import { ImageUploadSection }         from './components/ImageUploadSection';
import { IIMAGE_SPEC }                from 'features/imageCrop/ImageSpec';
import { RecoApis }                   from 'apis/recoAPI';
import { RecoUtils }                  from 'features/recommendations/utils/RecoUtils';
import { darken, 
         useTheme 
       }                              from '@mui/material/styles';
import { useSelector }                from 'react-redux';
import { selectPublishedRecos,
         selectDraftRecos,
         selectModifiedRecos
       }                              from 'store/dataReducers/recommendationsSlice';
import { selectRecommendationContentLimits } from 'store/dataReducers/cmsPilotSlice';
import { selectCategories }           from 'store/dataReducers/applianceProfileSlice';
import { CreateRecoRequestData, 
         Localization
       }                              from 'types/apis/recoInterfaces';
import { CMS_DEFAULT_LOCALE, 
         selectCMSLocales 
       }                              from 'store/dataReducers/cmsPilotSlice';
import { CONTENT_VERIFICATION_STATUS } from 'types/cms/frontendTypes';

const IFRAME_URL = `${process.env.REACT_APP_CX_WEB_URL}/public/widget/recommendations/summary-preview`

const defaultFormValues : CreateRecoRequestData = {
  title                 : '',
  description           : '',
  ctaButtonText         : '',
  ctaButtonLink         : '',
  fuelType              : [MEASUREMENT_TYPE.ELECTRIC],
  category              : 'totalEnergy',
  featuredContent       : false,
  program               : false,
  channelPaper          : true,
  channelEmail          : true,
  channelWeb            : true,
  localizations         : [] as Localization[]
}

export interface RecoFormProps {
  reco                 ?: APIData<"api::recommendation.recommendation">;
  selectedPilot         : APIData<"api::utility.utility">;
  selectedLocale        : string;
  editMode              : 'create' | 'update';
  onSubmit              : (data: CreateRecoRequestData) => Promise<void>;
  isDraft              ?: boolean;
  isPublished          ?: boolean;
  submitError          ?: ApiErrorInfo;
  onClearError         ?: () => void;
  onFormStateChange     : (isDirty: boolean) => void;
}

export const RecoEditForm = ({ reco, selectedPilot, editMode, onSubmit, 
                               isDraft, isPublished, submitError, onClearError, onFormStateChange, 
                               selectedLocale }: RecoFormProps) => {
  
  const { rc }         = useGlobalConfig()
  const theme          = useTheme()
  const publishedRecos = useSelector(selectPublishedRecos)
  const draftRecos     = useSelector(selectDraftRecos)
  const modifiedRecos  = useSelector(selectModifiedRecos)
  const categories     = useSelector(selectCategories)
  const locales        = useSelector(selectCMSLocales)
  const contentLimits  = useSelector(selectRecommendationContentLimits)

  const [isSaving, setIsSaving]                       = useState(false);
  const [error, setError]                             = useState<ApiErrorInfo>();
  const [isDirty, setIsDirty]                         = useState(false);
  const [imageUploads, setImageUploads]               = useState<Record<string, Record<number, string>>>({}); // locale wise image uploads
  const [locale, setLocale]                           = useState<string>(selectedLocale);
  const [localeTextValues, setLocaleTextValues]       = useState<Record<string, Record<string, string>>>({});
  const [selectedRecoId, setSelectedRecoId]           = useState<string>('');
  const [selectedVersion, setSelectedVersion]         = useState<'DRAFT' | 'MODIFIED' | 'PUBLISHED'>('PUBLISHED');

  const supportedLocales = selectedPilot.supportedLocales as string[]  
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const isFormDisabled = reco && (isPublished || reco.verificationStatus !== CONTENT_VERIFICATION_STATUS.TODO);
  const { control, handleSubmit, formState: { errors, isDirty: formIsDirty }, 
      trigger, reset, getValues, setValue, watch } = useForm({
    defaultValues: {...defaultFormValues as any},
    mode: 'onChange'
  }) as any;

  const featuredContentWatched = watch('featuredContent');
  const programWatched = watch('program');
  const ctaFieldsEditable = RecoUtils.areCtaFieldsEditable({
    featuredContent: featuredContentWatched,
    program: programWatched,
  });
  const ctaFieldsDisabledByFlags = !isFormDisabled && !ctaFieldsEditable;
  const ctaFieldsDisabled = Boolean(isFormDisabled || !ctaFieldsEditable);

  // Memoized list of all recommendations (similar to RecoEditPage)
  const memoizedRecos = useMemo(() => {
    const publishedRecosWithoutModifications = publishedRecos.filter(
      pubReco => modifiedRecos.find(modReco => modReco.documentId === pubReco.documentId) === undefined)
    return [...draftRecos, ...modifiedRecos, ...publishedRecosWithoutModifications]
  }, [draftRecos, modifiedRecos, publishedRecos]);

  // Get available versions for a selected recommendation
  const getAvailableVersions = useCallback((recoId: string) => {
    const reco = memoizedRecos.find(r => r.recommendationId === recoId);
    if (!reco) return [];
    
    const versions: Array<'DRAFT' | 'MODIFIED' | 'PUBLISHED'> = [];
    const docId = reco.documentId;
    
    // Check for draft version (standalone draft that doesn't have a published version)
    const draftReco = draftRecos.find(r => r.documentId === docId);
    if (draftReco && !publishedRecos.find(r => r.documentId === docId)) {
      versions.push('DRAFT');
    }
    
    // Check for modified version (draft with editStatus MODIFIED that has a published version)
    if (modifiedRecos.find(r => r.documentId === docId)) {
      versions.push('MODIFIED');
    }
    
    // Check for published version
    if (publishedRecos.find(r => r.documentId === docId)) {
      versions.push('PUBLISHED');
    }
    
    return versions;
  }, [memoizedRecos, draftRecos, modifiedRecos, publishedRecos]);

  // Convert APIData recommendation to CreateRecoRequestData
  const convertRecoToFormData = useCallback((reco: APIData<"api::recommendation.recommendation">): CreateRecoRequestData => {
    return {
      title: reco.title || '',
      description: reco.description || '',
      ctaButtonText: reco.ctaButtonText || '',
      ctaButtonLink: reco.ctaButtonLink || '',
      fuelType: reco.fuelType as MEASUREMENT_TYPE[] || [MEASUREMENT_TYPE.ELECTRIC],
      category: reco.category || 'totalEnergy',
      featuredContent: reco.featuredContent || false,
      program: reco.program || false,
      channelPaper: reco.channelPaper ?? true,
      channelEmail: reco.channelEmail ?? true,
      channelWeb: reco.channelWeb ?? true,
      icon: reco.icon,
      shortenedImage: reco.shortenedImage,
      fullImage: reco.fullImage,
      herImage: reco.herImage,
      elongatedImage: (reco as any).elongatedImage,
      localizations: (reco as any).localizations || [] as Localization[],
      videoLink: (reco as any).videoLink,
      detail: (reco as any).detail,
      disabled: reco.disabled,
      score: (reco as any).score,
      frequency: (reco as any).frequency,
      userType: (reco as any).userType,
      actionType: (reco as any).actionType,
      objective: (reco as any).objective,
      actionSavingsPotential: (reco as any).actionSavingsPotential,
      applianceProfileExclusion: (reco as any).applianceProfileExclusion,
      homeProfileExclusion: (reco as any).homeProfileExclusion,
      ratePlanExclusion: (reco as any).ratePlanExclusion,
      ppExclusion: (reco as any).ppExclusion,
      insightAssociation: (reco as any).insightAssociation,
    };
  }, []);

  // Handle recommendation ID selection
  const handleRecoIdChange = (event: SelectChangeEvent) => {
    const recoId = event.target.value;
    setSelectedRecoId(recoId);
    const availableVersions = getAvailableVersions(recoId);
    if (availableVersions.length > 0) {
      // Default to published if available, otherwise use first available
      setSelectedVersion(availableVersions.includes('PUBLISHED') ? 'PUBLISHED' : availableVersions[0]);
    }
  };

  // Handle version selection
  const handleVersionChange = (event: SelectChangeEvent) => {
    setSelectedVersion(event.target.value as 'DRAFT' | 'MODIFIED' | 'PUBLISHED');
  };

  // Handle copy from existing submit
  const handleCopyFromExisting = () => {
    if (!selectedRecoId) return;
    
    const selectedReco = memoizedRecos.find(r => r.recommendationId === selectedRecoId);
    if (!selectedReco) return;
    
    let recoToCopy: APIData<"api::recommendation.recommendation"> | undefined;
    const docId = selectedReco.documentId;
    
    if (selectedVersion === 'DRAFT') {
      // Draft version: find standalone draft (no published version)
      recoToCopy = draftRecos.find(r => r.documentId === docId && !publishedRecos.find(p => p.documentId === docId));
    } else if (selectedVersion === 'MODIFIED') {
      recoToCopy = modifiedRecos.find(r => r.documentId === docId);
    } else if (selectedVersion === 'PUBLISHED') {
      recoToCopy = publishedRecos.find(r => r.documentId === docId);
    }
    
    if (recoToCopy) {
      const formData = convertRecoToFormData(recoToCopy);
      // Use reset with keepDefaultValues: true to keep original defaults
      // This ensures the form will be marked as dirty if copied values differ from defaults
      reset(formData, { keepDefaultValues: true });
      // Clear local text values when resetting (similar to resetReco)
      setLocaleTextValues({});
      // Trigger preview update after React processes the form reset
      requestAnimationFrame(() => {
        // Use a small timeout to ensure getValues() returns the updated values
        setTimeout(() => {
          onPreview();
        }, 50);
      });
      // Clear the selection after copying
      setSelectedRecoId('');
      setSelectedVersion('PUBLISHED');
    }
  };

  const resetReco = useCallback((reco: APIData<"api::recommendation.recommendation">) => {
    reset({
      title: reco.title,
      description: reco.description,
      ctaButtonText: reco.ctaButtonText,
      ctaButtonLink: reco.ctaButtonLink,
      fuelType: reco.fuelType as MEASUREMENT_TYPE[],
      category: reco.category,
      featuredContent: reco.featuredContent,
      program: reco.program,
      channelPaper: reco.channelPaper,
      channelEmail: reco.channelEmail,
      channelWeb: reco.channelWeb,
      icon: reco.icon,
      verificationStatus: reco.verificationStatus,
      shortenedImage: reco.shortenedImage,
      fullImage: reco.fullImage,
      herImage: reco.herImage,
      elongatedImage: (reco as any).elongatedImage,
      localizations: (reco as any).localizations || [] as Localization[],
      videoLink: (reco as any).videoLink,
      detail: (reco as any).detail,
      disabled: reco.disabled,
      score: (reco as any).score,
      frequency: (reco as any).frequency,
      userType: (reco as any).userType,
      actionType: (reco as any).actionType,
      objective: (reco as any).objective,
      actionSavingsPotential: (reco as any).actionSavingsPotential,
      applianceProfileExclusion: (reco as any).applianceProfileExclusion,
      homeProfileExclusion: (reco as any).homeProfileExclusion,
      ratePlanExclusion: (reco as any).ratePlanExclusion,
      ppExclusion: (reco as any).ppExclusion,
      insightAssociation: (reco as any).insightAssociation,
    });
  }, [ reset ]);

  const getLocaleTextValue = useCallback((fieldName: keyof Localization) => {
    
    if (locale === CMS_DEFAULT_LOCALE) {
      return getValues()[fieldName] || '';
    }
    
    // For non-default locales, check local state first, then form state
    const localValue = localeTextValues[locale]?.[fieldName];
    if (localValue !== undefined) {
      return localValue;
    }
    
    // Fallback to form state
    const localizationEntry = getValues().localizations?.find((l : Localization) => l.locale === locale);
    return localizationEntry?.[fieldName] || '';
  }, [getValues, locale, localeTextValues]);

  const setLocaleTextValue = (fieldName: keyof Localization, value: any) => {
    
    if (locale === CMS_DEFAULT_LOCALE) {
      setValue(fieldName, value, { shouldDirty: true, shouldValidate: true });
      debouncedPreview();
      return;
    }
    
    // For non-default locales, update local state immediately for responsive UI
    setLocaleTextValues(prev => ({
      ...prev,
      [locale]: {
        ...prev[locale],
        [fieldName]: value
      }
    }));
    
    // Also update the form state for persistence
    const allValues = getValues();
    const existingLocalizations: Localization[] = allValues.localizations || [];
    const existingIndex = existingLocalizations.findIndex((l: Localization) => l.locale === locale);

    let updatedLocalizations: Localization[];
    if (existingIndex >= 0) {
      // Replace existing locale entry
      const existing = existingLocalizations[existingIndex];
      const updated: Localization = {
        ...existing,
        [fieldName]: value,
      };
      existingLocalizations.splice(existingIndex, 1, updated);
      updatedLocalizations = existingLocalizations;
    } else {
      // Create new locale entry
      const newLocaleEntry: Localization = {
        locale: locale,
        [fieldName]: value,
      } as Localization;
      updatedLocalizations = [...existingLocalizations, newLocaleEntry];
    }

    setValue('localizations', updatedLocalizations, { shouldDirty: true, shouldValidate: true });
    debouncedPreview();
  };

  const getLocaleImageValue = useCallback((fieldName: keyof Localization) => {
    
    let data = getValues();
    if (locale !== CMS_DEFAULT_LOCALE) { 
      data = getValues().localizations.find((l : Localization) => l.locale === locale) || ({} as Localization);
    }
    
    const imageValue = data[fieldName];
    
    if (typeof imageValue === 'number') {
      return imageUploads[locale]?.[imageValue];
    }
    
    if (imageValue && typeof imageValue === 'object' && 'url' in imageValue) {
      return imageValue.url;
    }
    
    if (typeof imageValue === 'string') {
      return imageValue;
    }
    
    return undefined;

  }, [getValues, locale, imageUploads]);

  const setLocaleImageValue = (fieldName: keyof Localization, resImageId: number) => {
    
    if (locale === CMS_DEFAULT_LOCALE) {
      setValue(fieldName, resImageId, { shouldDirty: true, shouldValidate: true });
      debouncedPreview();
      return;
    }
    
    const allValues = getValues();
    const existingLocalizations: Localization[] = allValues.localizations || [];
    const existingIndex = existingLocalizations.findIndex((l: Localization) => l.locale === locale);

    let updatedLocalizations: Localization[];
    if (existingIndex >= 0) {
      // Replace existing locale entry
      const existing = existingLocalizations[existingIndex];
      const updated: Localization = {
        ...existing,
        [fieldName]: resImageId,
      };
      existingLocalizations.splice(existingIndex, 1, updated);
      updatedLocalizations = existingLocalizations;
    } else {
      // Create new locale entry
      const newLocaleEntry: Localization = {
        locale: locale,
        [fieldName]: resImageId,
      } as Localization;
      updatedLocalizations = [...existingLocalizations, newLocaleEntry];
    }

    setValue('localizations', updatedLocalizations, { shouldDirty: true, shouldValidate: true });
    debouncedPreview();
  };

  const onPreview = useCallback(() => {
    // Get locale-specific data for preview using the helper functions
    const title = getLocaleTextValue('title');
    const description = getLocaleTextValue('description');
    const ctaButtonText = getLocaleTextValue('ctaButtonText');
    const shortenedImage = getLocaleImageValue('shortenedImage');
    const fullImage = getLocaleImageValue('fullImage');
    const ctaButtonLink = getValues('ctaButtonLink');

    const params = {
      dashboardIcon: fullImage,
      dashboardIconShortened: shortenedImage,
      title: title,
      description: description,
      ctaButtonText: ctaButtonText,
      ctaButtonLink: ctaButtonLink,
    }

    iframeRef.current?.contentWindow?.postMessage({
      type : 'RECO_PREVIEW', 
      data : params
    }, process.env.REACT_APP_CX_WEB_URL!)   
  }, [getLocaleTextValue, getLocaleImageValue, getValues]);

  // Create memoized debounced trigger function
  const debouncedTrigger = useMemo(
    () => debounce((fieldName: keyof CreateRecoRequestData) => {
      trigger(fieldName);
    }, 300),
    [trigger]
  );

  // Create memoized debounced preview function
  const debouncedPreview = useMemo(
    () => debounce(() => {
      onPreview();
    }, 500),
    [onPreview]
  );

  useEffect(() => {
    setError(submitError);
  }, [submitError]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data.type === 'RECO_PREVIEW' && event.data.status === 'READY') {
        onPreview();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onPreview])

  useEffect(() => {
    if (reco) {
      resetReco(reco);
      // Clear local text values when resetting
      setLocaleTextValues({});
    } 
  }, [reco, resetReco]);

  // Separate effect for preview updates
  useEffect(() => {
    onPreview();
  }, [onPreview]);

  // Track form changes and notify parent
  useEffect(() => {
    setIsDirty(formIsDirty);
    onFormStateChange?.(formIsDirty);
  }, [formIsDirty, onFormStateChange]);

  // Cleanup debounced functions on unmount
  useEffect(() => {
    return () => {
      debouncedTrigger.cancel();
      debouncedPreview.cancel();
    };
  }, [debouncedTrigger, debouncedPreview]);

  const handleLocaleChange = (newLocale: string) => {
    setLocale(newLocale);
  };

  // Sync local text values with form state when locale changes
  useEffect(() => {
    if (locale !== CMS_DEFAULT_LOCALE) {
      const formValues = getValues();
      const localizationEntry = formValues.localizations?.find((l: Localization) => l.locale === locale);
      if (localizationEntry) {
        setLocaleTextValues(prev => ({
          ...prev,
          [locale]: {
            ...prev[locale],
            ...localizationEntry
          }
        }));
      }
    }
  }, [locale, getValues]);

  // Add function to check if field is modified
  const isFieldModified = (fieldName: keyof CreateRecoRequestData) => {
    if (!reco || (reco as any).editStatus !== 'MODIFIED') {
      return false;
    }

    const publishedVersion = publishedRecos.find(
      (r: any) => r.recommendationId === (reco as any).recommendationId
    )
    if (!publishedVersion) return false;
    
    const diff = RecoUtils.getRecoDiff(reco as any, publishedVersion as any) as any;
    return Object.keys(diff).includes(fieldName);
  }

  // Common sx style for modified fields
  const getModifiedFieldStyle = (fieldName: keyof CreateRecoRequestData) => ({
    '& .MuiInputBase-inputMultiline' : {
      padding: '0px',
    },
    '& .MuiInputBase-input' : {
      color: theme.palette.text.secondary,
    },
    '& .MuiSelect-select' : {
      minHeight: 'unset',
      backgroundColor: 'inherit',
    },
    '&.Mui-disabled': {
      color: theme.palette.action.disabled,
    },
    '& .MuiInputBase-input.Mui-disabled': {
      color: theme.palette.action.disabled,
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
      
      '&.Mui-disabled': {
        color: theme.palette.action.disabled,
      }
    },
    ...(isFieldModified(fieldName) && {
      backgroundColor: (theme: any) => {
        if (theme.palette.mode === 'light') {
          return 'rgba(196, 255, 176, 0.5)';
        }
        return darken('rgba(196, 255, 176, 1)', 0.5);
      },
    }),
  });

  // Common sx style for modified Switch components
  const getModifiedSwitchStyle = (fieldName: keyof CreateRecoRequestData) => ({
    ...(isFieldModified(fieldName) && {
      '& .MuiSwitch-switchBase': {
        backgroundColor: (theme: any) => {
          if (theme.palette.mode === 'light') {
            return 'rgba(196, 255, 176, 0.5)';
          }
          return darken('rgba(196, 255, 176, 1)', 0.5);
        },
      },
    }),
  });

  const handleFormSubmit = async (data: CreateRecoRequestData) => {

    setIsSaving(true);
    setError(undefined);

    // Get all form values
    const allValues = getValues();
    
    const formData = {
      ...allValues
    };
    // Localized fields are already upserted in form state via setLocaleValue
    // Ensure localizations array exists on payload if any locale data present
    if (allValues.localizations && allValues.localizations.length > 0) {
      formData.localizations = allValues.localizations;
    }

    if (editMode === 'create') {
      await onSubmit(formData);
    } else {
      const newReco : CreateRecoRequestData = {
        ...formData,
        id: reco!.id!,
        documentId: reco!.documentId,
        editStatus: (reco as any)!.editStatus,
        recommendationId: reco!.recommendationId
      }
      
      const diff = RecoUtils.getRecoDiff(newReco, reco!) as any
      diff.id = reco!.id!
      diff.documentId = reco!.documentId
      
      await onSubmit(diff as CreateRecoRequestData);
    }
    
    // Clear locale changes after successful save
    setIsSaving(false);
  };

  const handleFormError = (err : FieldErrors<CreateRecoRequestData>) => {

    const errorMessage = Object.values(err)
      .map(error => error.message)
      .filter(Boolean)
      .join(', ');
    setError({code: 'FORM_ERROR', message: errorMessage});
    setIsSaving(false);
  }

  const onDiscard = () => {
    if (isDirty) {
      if (window.confirm('Are you sure you want to discard your changes?')) {
        if (reco) {
          resetReco(reco);
        } else {
          reset({ ...defaultFormValues });
        }
        setIsDirty(false);
        onFormStateChange?.(false);
      }
    }
  }

  const handleImageUpdate = async (imageType: IIMAGE_SPEC, fieldName: keyof Localization, blob: Blob) : Promise<ApiErrorInfo | undefined> => {
    
    const fileName = `${imageType.toLowerCase()}-${Date.now()}`;
    const resp = await RecoApis.uploadTempImage(rc, blob, fileName)
    
    if (resp.error) {
      return resp.error;
    }

    // Update imageUploads state with the new URL
    setImageUploads(prev => {
      const newState = {...prev};
      if (!newState[locale]) {
        newState[locale] = {};
      }
      newState[locale] = {
        ...newState[locale],
        [resp.data[0].id]: resp.data[0].url
      };
      return newState;
    });

    setLocaleImageValue(fieldName, resp.data[0].id);

    // Mark form as dirty
    setIsDirty(true);
    onFormStateChange?.(true);

    return undefined;
  };

  return (
    <Box sx={{ p : 3}}>
      
      {/* Status Alerts */}
      { editMode === 'update' && (
        isPublished ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            This is the published version of the recommendation
          </Alert>
        ) : isDraft ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This is the draft version of the recommendation. This version is not yet published and is not visible on Production.
          </Alert>
        ) : (reco as any).editStatus === 'MODIFIED' ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            This is the modified version of the published recommendation.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            No modifications have been made to this recommendation yet. Values are autofilled from the published version.
          </Alert>
        )
      )}

      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row'}}>
        <Box sx={{ width: '600px', display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Copy from existing - only show in create mode */}
          {editMode === 'create' && (
            <Card>
              <CardContent>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    marginBottom: 2,
                    textAlign: 'left',
                    fontWeight: 'bold',
                    color: 'inherit'
                  }}
                >
                  Copy from existing
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="copy-reco-select-label">Recommendation ID</InputLabel>
                    <Select
                      labelId="copy-reco-select-label"
                      id="copy-reco-select"
                      value={selectedRecoId}
                      label="Recommendation ID"
                      onChange={handleRecoIdChange}
                      sx={{ 
                        textAlign: 'left',
                        color: theme.palette.text.secondary,
                        '& .MuiSelect-select': {  
                          minHeight: 'unset',
                        }
                      }}
                    >
                      {memoizedRecos.map((reco) => (
                        <MenuItem key={reco.documentId} value={reco.recommendationId}>
                          {reco.recommendationId}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  {selectedRecoId && (
                    <FormControl fullWidth size="small">
                      <InputLabel id="copy-version-select-label">Version</InputLabel>
                      <Select
                        labelId="copy-version-select-label"
                        id="copy-version-select"
                        value={selectedVersion}
                        label="Version"
                        onChange={handleVersionChange}
                        sx={{ 
                          textAlign: 'left',
                          color: theme.palette.text.secondary,
                          '& .MuiSelect-select': {  
                            minHeight: 'unset',
                          }
                        }}
                      >
                        {getAvailableVersions(selectedRecoId).map((version) => (
                          <MenuItem key={version} value={version}>
                            {version}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  
                  <BglPrimaryButton
                    onClick={handleCopyFromExisting}
                    disabled={!selectedRecoId}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Copy
                  </BglPrimaryButton>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Classification */}
          <Card>
            <CardContent>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  marginBottom : 2,
                  textAlign: 'left',
                  fontWeight: 'bold',
                  color: 'inherit'
                }}
              >
                Classification
              </Typography>
              <Box sx={{ display: 'grid', gap: 2, color: 'inherit' }}>
                <Controller
                  name="fuelType"
                  control={control}
                  rules={{ 
                    required: 'Fuel Type is required',
                    validate: (value : MEASUREMENT_TYPE[]) => (value.length > 0) || 'At least one Fuel Type must be selected'
                  }}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.fuelType} required>
                      <InputLabel 
                        sx={{
                          fontSize: '0.875rem', 
                          color: theme.palette.text.secondary,
                          '&.Mui-disabled': {
                            color: theme.palette.action.disabled,
                          }
                        }}
                      >
                        Fuel Type
                      </InputLabel>
                      <Select
                        {...field}
                        multiple
                        label="Fuel Type"
                        size="small"
                        disabled={editMode === 'update'}
                        renderValue={(selected) => (selected as string[]).join(', ')}
                        sx={{ 
                          textAlign: 'left', 
                          color: 'inherit',
                          '& .Mui-disabled': {
                            color: theme.palette.action.disabled,
                          },
                          ...getModifiedFieldStyle('fuelType')
                        }}
                      >
                        {Object.values(MEASUREMENT_TYPE).map((type) => (
                          <MenuItem key={type} value={type} sx={{ fontSize: '0.875rem' }}>
                            <Checkbox checked={field.value.indexOf(type) > -1} />
                            <ListItemText primary={type} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                <Controller
                  name="category"
                  control={control}
                  rules={{ required: 'Category is required' }}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.category} required disabled={isFormDisabled}>
                      <InputLabel 
                        sx={{ 
                          fontSize: '0.875rem', 
                          color: theme.palette.text.secondary,
                          '&.Mui-disabled': {
                            color: theme.palette.action.disabled,
                          }
                        }}
                      >
                        Category
                      </InputLabel>
                      <Select
                        {...field}
                        label="Category"
                        size="small"
                        disabled={editMode === 'update'}
                        sx={{ 
                          textAlign: 'left', 
                          color: 'inherit',
                          ...getModifiedFieldStyle('category')
                        }}
                      >
                        {categories.map((category) => (
                          <MenuItem key={category.categoryId} value={category.categoryId} sx={{ fontSize: '0.875rem' }}>
                            {category.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />

                { editMode === 'update' && 
                  <Controller
                    name="verificationStatus"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Verification Status"
                        fullWidth
                        size="small"
                        disabled={true}
                        value={field.value}
                        slotProps={{
                          inputLabel: {
                            shrink: true
                          }
                        }}
                        sx={{
                          '&.Mui-disabled': {
                            color: theme.palette.action.disabled,
                          },
                          '& .MuiInputBase-input.Mui-disabled': {
                            color: theme.palette.action.disabled,
                          },
                          '& .MuiInputLabel-root': {
                            color: theme.palette.text.secondary,
                            '&.Mui-disabled': {
                              color: theme.palette.action.disabled,
                            }
                          }
                        }}
                      />
                    )}
                  />
                }

              </Box>
            </CardContent>
          </Card>

          {/* Content */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    textAlign: 'left',
                    fontWeight: 'bold',
                    color: 'inherit'
                  }}
                >
                  Content
                </Typography>
                {supportedLocales && supportedLocales.length > 0 && (
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Locale</InputLabel>
                    <Select
                      value={locale}
                      onChange={(e) => handleLocaleChange(e.target.value)}
                      label="Locale"
                      sx={{
                        '& .MuiSelect-select' : {
                          minHeight: 'unset',
                          backgroundColor: 'inherit',
                        },
                      }}
                    >
                      {supportedLocales.map((locale) => (
                        <MenuItem key={locale} value={locale} sx={{ fontSize: '0.875rem' }}>
                          {locales.find(l => l.code === locale)?.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Controller
                  name="title"
                  control={control}
                  rules={{ 
                    required: 'Title is required',
                    maxLength: {
                      value: contentLimits.title,
                      message: `Title must be less than ${contentLimits.title} characters`
                    }
                  }}
                  render={({ field }) => (
                    <TextField
                      label="Title"
                      required
                      fullWidth
                      size="small"
                      disabled={isFormDisabled}
                      error={!!errors.title}
                      helperText={
                        <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>{errors.title?.message as string || ''}</span>
                          <span>{`${(getLocaleTextValue('title') as string || '').length} / ${contentLimits.title}`}</span>
                        </Box>
                      }
                      value={getLocaleTextValue('title')}
                      onChange={(e) => {
                        setLocaleTextValue('title', e.target.value);
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': getModifiedFieldStyle('title'),
                        
                      }}
                    />
                  )}
                />
                <Controller
                  name="description"
                  control={control}
                  rules={{ 
                    required: 'Description is required',
                    maxLength: {
                      value: contentLimits.description,
                      message: `Description must be less than ${contentLimits.description} characters`
                    }
                  }}
                  render={({ field }) => (
                    <TextField
                      label="Description"
                      required
                      multiline
                      rows={5}
                      size="small"
                      disabled={isFormDisabled}
                      error={!!errors.description}
                      helperText={
                        <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>{errors.description?.message as string || ''}</span>
                          <span>{`${(getLocaleTextValue('description') as string || '').length} / ${contentLimits.description}`}</span>
                        </Box>
                      }
                      value={getLocaleTextValue('description')}
                      onChange={(e) => {
                        setLocaleTextValue('description', e.target.value);
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': getModifiedFieldStyle('description'),
                      }}
                    />
                  )}
                />
                <Controller
                  name="ctaButtonText"
                  control={control}
                  rules={{ 
                    maxLength: {
                      value: contentLimits.ctaButtonText,
                      message: `CTA Button text must be less than ${contentLimits.ctaButtonText} characters`
                    }
                  }}
                  render={({ field }) => (
                    <TextField
                      label="CTA Button Text"
                      fullWidth
                      size="small"
                      disabled={ctaFieldsDisabled}
                      error={!!errors.ctaButtonText}
                      helperText={
                        <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>
                            {(errors.ctaButtonText?.message as string) ||
                              (ctaFieldsDisabledByFlags
                                ? 'Enable Featured Content or Program to edit.'
                                : '')}
                          </span>
                          {ctaFieldsEditable && (
                            <span>{`${(getLocaleTextValue('ctaButtonText') as string || '').length} / ${contentLimits.ctaButtonText}`}</span>
                          )}
                        </Box>
                      }
                      value={getLocaleTextValue('ctaButtonText')}
                      onChange={(e) => {
                        setLocaleTextValue('ctaButtonText', e.target.value);
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': getModifiedFieldStyle('ctaButtonText'),
                      }}
                    />
                  )}
                />
                <Controller
                  name="ctaButtonLink"
                  control={control}
                  rules={{ 
                    //validate: value => validateUrl(value) || 'Please enter a valid URL'
                  }}
                  render={({ field }) => (
                    <TextField
                      label="CTA Button Link"
                      fullWidth
                      size="small"
                      disabled={ctaFieldsDisabled}
                      error={!!errors.ctaButtonLink}
                      helperText={
                        (errors.ctaButtonLink?.message as string) ||
                        (ctaFieldsDisabledByFlags ? 'Enable Featured Content or Program to edit.' : undefined)
                      }
                      value={getLocaleTextValue('ctaButtonLink')}
                      onChange={(e) => {
                        setLocaleTextValue('ctaButtonLink', e.target.value);
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': getModifiedFieldStyle('ctaButtonLink'),
                      }}
                    />
                  )}
                />
                <Controller
                  name="fullImage"
                  control={control}
                  render={({ field }) => {
                    return (
                      <ImageUploadSection
                        isDisabled={!!isFormDisabled}
                        imageType="RECO_FULL"
                        currentImageUrl={getLocaleImageValue('fullImage')}
                        onImageUpdate={(blob) => handleImageUpdate('RECO_FULL', 'fullImage', blob)}
                        isModified={isFieldModified('fullImage')}
                      />
                    );
                  }}
                />
                <Divider />

                <Controller
                  name="shortenedImage"
                  control={control}
                  render={({ field }) => {
                    return (
                      <ImageUploadSection
                        isDisabled={!!isFormDisabled}
                        imageType="RECO_SHORT"
                        currentImageUrl={getLocaleImageValue('shortenedImage')}
                        onImageUpdate={(blob) => handleImageUpdate('RECO_SHORT', 'shortenedImage', blob)}
                        isModified={isFieldModified('shortenedImage')}
                      />
                    );
                  }}
                />
                <Divider />

                <Controller
                  name="herImage"
                  control={control}
                  render={({ field }) => {
                    return (
                      <ImageUploadSection
                        isDisabled={!!isFormDisabled}
                        imageType="RECO_HER"
                        currentImageUrl={getLocaleImageValue('herImage')}
                        onImageUpdate={(blob) => handleImageUpdate('RECO_HER', 'herImage', blob)}
                        isModified={isFieldModified('herImage')}
                      />
                    );
                  }}
                />
                <Divider />

                <Controller
                  name="icon"
                  control={control}
                  render={({ field }) => {
                    return (
                      <ImageUploadSection
                        isDisabled={!!isFormDisabled}
                        imageType="ICON"
                        currentImageUrl={getLocaleImageValue('icon')}
                        onImageUpdate={(blob) => handleImageUpdate('ICON', 'icon', blob)}
                        isModified={isFieldModified('icon')}
                      />
                    );
                  }}
                />
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  marginBottom : 2,
                  textAlign: 'left',
                  fontWeight: 'bold',
                  color: 'inherit'
                }}
              >
                Configuration
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

              <Controller
                  name="featuredContent"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          size="small"
                          disabled={isFormDisabled}
                          sx={getModifiedSwitchStyle('featuredContent')}
                        />
                      }
                      label="Featured Content"
                      sx={{ 
                        fontSize: '0.875rem', 
                        color: 'inherit',
                        '&.Mui-disabled': {
                          color: theme.palette.action.disabled,
                        }
                      }}
                    />
                  )}
                />
                <Controller
                  name="program"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          size="small"
                          disabled={isFormDisabled}
                          sx={getModifiedSwitchStyle('program')}
                        />
                      }
                      label="Program"
                      sx={{ 
                        fontSize: '0.875rem', 
                        color: 'inherit',
                        '&.Mui-disabled': {
                          color: theme.palette.action.disabled,
                        }
                      }}
                    />
                  )}
                />
                <Controller
                  name="channelEmail"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          size="small"
                          disabled={isFormDisabled}
                          sx={getModifiedSwitchStyle('channelEmail')}
                        />
                      }
                      label="Channel Email"
                      sx={{ 
                        fontSize: '0.875rem', 
                        color: 'inherit',
                        '&.Mui-disabled': {
                          color: theme.palette.action.disabled,
                        }
                      }}
                    />
                  )}
                />
                <Controller
                  name="channelWeb"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          size="small"
                          disabled={isFormDisabled}
                          sx={getModifiedSwitchStyle('channelWeb')}
                        />
                      }
                      label="Channel Web"
                      sx={{ 
                        fontSize: '0.875rem', 
                        color: 'inherit',
                        '&.Mui-disabled': {
                          color: theme.palette.action.disabled,
                        }
                      }}
                    />
                  )}
                />
              </Box>
            </CardContent>
          </Card>

          {error && 
            <Alert severity="error" onClose={onClearError}>{typeof error === 'string' ? error : JSON.stringify(error)}</Alert>
          }      
          {/* Action Buttons */}
          {(!reco || !isPublished) && <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <BglCloseButton
              startIcon={<RestoreIcon />}
              onClick={onDiscard}
              type="button"
              disabled={!isDirty || isSaving}
            >
              Discard
            </BglCloseButton>

            <BglPrimaryButton
              startIcon={<SaveIcon />}
              onClick={handleSubmit(handleFormSubmit, handleFormError)}
              disabled={!isDirty}
              loading={isSaving}
              type="button"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </BglPrimaryButton>

          </Box>}
        </Box>

        <Box sx={{ 
          minWidth: '1000px',
          overflowX: 'auto',
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'left'
        }}>
          <iframe 
            ref={iframeRef}
            src={IFRAME_URL}
            style={{
              width: '100%',
              overflowX: 'auto',
              height: '510px',
              overflowY: 'clip',
              border: 'none'
            }}
            title='Live Preview'
          />
          <div style={{ padding: '20px', backgroundColor: '#eef5f9' }}>
            <div style={{ fontSize: 22, fontWeight: 500, marginBottom: 20, color: '#000' }}>
              EMAIL
            </div>
            {/* Tip icon Preview */}
            <div style={{ padding: '20px', backgroundColor: 'white', width : '536px' }}>
              <Typography variant="h6" sx={{ paddingLeft: '20px', fontWeight: 700, fontSize: '24px', color: '#2e384d'}}>
                Personalised tips for you
              </Typography>
              <div style={{ display: 'flex', border : '1px solid #f6f6f6', borderRadius: '24px', marginTop : '24px', overflow: 'hidden' }}>
                <img
                  style={{ width: 80, height: 80, objectFit: 'cover', 
                    borderRadius: '15%', backgroundColor: '#186cdd', marginLeft: '20px', marginTop: '25px' }}
                  src={getLocaleImageValue('icon') || 'https://d13hc4rsp6iv99.cloudfront.net/electric-20191127/communityconnect_RMP_square.png'}
                  alt="icon"
                 />
                 <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                  <Typography variant='h3' sx={{ fontWeight: 700, fontSize: '21px', paddingTop: '25px', 
                    paddingBottom: '15px', paddingLeft: '25px', paddingRight: '45px', lineHeight: '25.2px', color: '#2e384d',
                    wordBreak: 'break-word', overflowWrap: 'break-word', overflow: 'hidden' }}>
                    {getLocaleTextValue('title') || 'Giving to strengthen community'}
                  </Typography>
                  <Typography sx={{ fontSize: '19px', lineHeight: '28.5px', paddingLeft: '25px', paddingRight: '45px', paddingBottom: '25px', color: '#6c7f93',
                    wordBreak: 'break-word', overflowWrap: 'break-word', overflow: 'hidden' }}>
                    {getLocaleTextValue('description') || "Whether supporting local renewable projects or hanging holiday lights, we're part of your community."}
                  </Typography>
                 </div>
              </div>
            </div>

            {/* Sponsored reco Preview */}
            <div style={{ padding: '20px', backgroundColor: 'white', width : '536px' }}>
              <div style={{ display: 'flex', border : '1px solid #f6f6f6', borderRadius: '24px', marginTop : '24px', overflow: 'hidden' }}>
                <div style={{ width: '250px', padding: '20px', minWidth: 0, overflow: 'hidden' }}>
                  <Typography variant='h3' sx={{ fontWeight: 700, fontSize: '21px', 
                    paddingBottom: '15px', lineHeight: '25.2px', color: '#2e384d',
                    wordBreak: 'break-word', overflowWrap: 'break-word', overflow: 'hidden' }}>
                    {getLocaleTextValue('title') || 'Giving to strengthen community'}
                  </Typography>
                  <Typography sx={{ fontSize: '18px', lineHeight: '27px', paddingBottom: '8px', color: '#6c7f93',
                    wordBreak: 'break-word', overflowWrap: 'break-word', overflow: 'hidden' }}>
                    {getLocaleTextValue('description') || "Whether supporting local renewable projects or hanging holiday lights, we're part of your community."}
                  </Typography>
                  <div style={{ display: 'flex', width: '146px', height: '52px', alignItems: 'center', 
                    textAlign: 'center', marginTop: '20px', justifyContent: 'center', color: '#8bb5ed', 
                    fontSize: '17px', lineHeight: '26px', fontWeight: 400, backgroundColor: '#f3f8fd',
                    border: '1px solid', borderColor: '#0072ce', borderRadius: '16px' }}>
                    <Link href={getValues('ctaButtonLink')} target='_blank' style={{ textDecoration: 'none', color: '#0072ce' }}>
                      {getLocaleTextValue('ctaButtonText') || 'LEARN MORE'}
                    </Link>
                  </div>
                </div>

                <div style={{ width: '284px' }}>
                  <img style={{ width: '100%', height: '100%', objectFit: 'cover', borderTopRightRadius: '24px', borderBottomRightRadius: '24px' }}
                    src={getLocaleImageValue('shortenedImage') || 'https://d13hc4rsp6iv99.cloudfront.net/holiday_pp/holiday_email/holiday-285x340.png'}
                    alt="shortImage"/>
                </div>
                
              </div>

            </div>
          </div>
        </Box>
      </Box>
    </Box>
  );
}; 